import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import CountdownTimer from "./CountdownTimer.tsx";
import { Draw } from "@/types/draw.ts";
import { useTranslation } from "@/i18n";

interface ProductCardProps {
  draw: Draw;
  onSelect: (product: Draw) => void;
}

const LotteryCard = ({ draw, onSelect }: ProductCardProps) => {
  const { t } = useTranslation();
  return (
    <Card
      className="flex h-full cursor-pointer flex-col gap-1 transition-all duration-200 hover:shadow-lg"
      onClick={() => onSelect(draw)}
    >
      <div className="relative px-4 pt-4">
        <div className="relative mb-4 w-full overflow-hidden rounded-md">
          <img
            src={draw.logo_url || "/placeholder.svg"}
            alt={draw.name}
            className="object-cover transition-transform duration-300 group-hover:scale-105"
          />
        </div>
      </div>

      <CardContent className="flex flex-grow flex-col">
        <div className="mb-2 flex gap-1">
          {draw.status === "live" && (
            <Badge className="bg-green-700">
              {t(`raffles.status.${draw.status}`)}
            </Badge>
          )}
          {draw.status === "closed" && (
            <Badge variant="destructive">
              {t(`raffles.status.${draw.status}`)}
            </Badge>
          )}
          {draw.status === "scheduled" && (
            <Badge className="bg-blue-700">
              {t(`raffles.status.${draw.status}`)}
            </Badge>
          )}

          {!["closed", "live", "scheduled"].includes(draw.status) && (
            <Badge variant="outline">
              {t(`raffles.status.${draw.status}`)}
            </Badge>
          )}

          {draw.lottery.type === "subscription" && (
            <Badge>{t("common.subscription")}</Badge>
          )}
        </div>

        <h3 className="mb-1 text-xl font-semibold">{draw.name}</h3>
        <p className="text-muted-foreground mb-2 text-sm">{draw.description}</p>
        <p className="mb-2">{draw.price_cta}</p>
        <p className="mt-auto text-lg font-bold">
          {t("common.price", { amount: draw.ticket_price })}
        </p>
        {draw.end_date && (
          <CountdownTimer
            upComing={draw.status === "scheduled"}
            time={
              draw.status === "scheduled"
                ? new Date(draw.start_date)
                : new Date(draw.end_date)
            }
            compact={true}
            className="text-primary mt-1"
          />
        )}
      </CardContent>

      <CardFooter className="pt-0">
        <Button className="w-full" onClick={() => onSelect(draw)}>
          {draw.status === "scheduled"
            ? t("raffles.preview")
            : t("raffles.play")}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default LotteryCard;
